/**
 * UndoBanner — 30-second sticky banner after a soft-delete.
 *
 * Drop-in for any page that triggers a soft-delete and gets a
 * `deleted_request_id` back. Pre-loads the id, ticks down, and offers
 * one-click restore. Auto-dismisses after the timer or on success.
 *
 * Usage:
 *   const [undo, setUndo] = useState(null);
 *   // after delete response:
 *   setUndo({
 *     kind: "transactions",                    // "transactions" | "invoices"
 *     deletedRequestId: data.deleted_request_id,
 *     count: data.deleted,
 *     onRestored: () => reloadList(),
 *   });
 *   <UndoBanner state={undo} onClose={() => setUndo(null)} />
 *
 * Operational UX only. Same affordance Gmail's "Message moved to Trash · Undo"
 * gives you — no redesign, no page chrome change.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowUUpLeft, X } from "@phosphor-icons/react";

const TIMEOUT_S = 30;

const RESTORE_PATHS = {
  transactions: "/transactions/restore-by-request",
  invoices: "/invoices/restore-by-request",
};

const NOUN = {
  transactions: { singular: "transaction", plural: "transactions" },
  invoices: { singular: "invoice", plural: "invoices" },
};

export default function UndoBanner({ state, onClose }) {
  const [remaining, setRemaining] = useState(TIMEOUT_S);
  const [busy, setBusy] = useState(false);

  // Reset the timer whenever a new state lands.
  useEffect(() => {
    if (!state) return;
    setRemaining(TIMEOUT_S);
    setBusy(false);
  }, [state?.deletedRequestId]);

  // Tick.
  useEffect(() => {
    if (!state) return;
    if (remaining <= 0) {
      onClose?.();
      return;
    }
    const t = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(t);
  }, [remaining, state, onClose]);

  if (!state) return null;

  const noun = NOUN[state.kind] || NOUN.transactions;
  const label = `${state.count} ${state.count === 1 ? noun.singular : noun.plural}`;
  const path = RESTORE_PATHS[state.kind] || RESTORE_PATHS.transactions;

  const handleUndo = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(path, {
        deleted_request_id: state.deletedRequestId,
      });
      const extra =
        state.kind === "transactions"
          ? data.invoices_restored
            ? ` · ${data.invoices_restored} invoice(s) restored`
            : ""
          : data.reattached_lines
            ? ` · ${data.reattached_lines} lines re-attached`
            : "";
      toast.success(`Restored ${data.restored_count} ${noun.plural}${extra}`);
      state.onRestored?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
      setBusy(false);
    }
  };

  // Width of the depletion bar, 0..100%.
  const pct = Math.max(0, Math.min(100, (remaining / TIMEOUT_S) * 100));

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] w-[min(560px,calc(100vw-32px))]"
      data-testid="undo-banner"
    >
      <div className="relative bg-zinc-900 text-white border border-zinc-800 shadow-xl overflow-hidden">
        {/* depletion bar */}
        <div className="absolute top-0 left-0 h-0.5 bg-amber-400 transition-[width] duration-1000 ease-linear"
             style={{ width: `${pct}%` }} />
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold">
              Soft-deleted {label}
            </div>
            <div className="text-xs text-zinc-400 mt-0.5 font-mono truncate">
              id: {state.deletedRequestId} · {remaining}s to undo
            </div>
          </div>
          <button
            className="gf-btn gf-btn-primary !text-xs flex items-center gap-1"
            disabled={busy}
            onClick={handleUndo}
            data-testid="undo-banner-undo"
          >
            <ArrowUUpLeft size={12} /> {busy ? "Restoring…" : "Undo"}
          </button>
          <button
            className="text-zinc-400 hover:text-white"
            onClick={onClose}
            aria-label="dismiss"
            data-testid="undo-banner-dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
